import { duration } from '@play-co/components';
import StateObserver, { now, user } from 'src/StateObserver';
import { useCache } from 'src/lib/utils';
import { AvatarFrameId } from 'src/replicant/profile/frames';
import {
  getOngoingWeeklyLeaderboardEvent,
  getWeeklyLeaderboardRankingArgs,
} from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.getters';
import {
  weeklyLeaderboardSchedule,
  weeklyLeaderboardSize,
} from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.ruleset';

/**
 * Data for weekly leaderboard player entries
 */
export type WeeklyLeaderboardMemberData = {
  id: string;
  name: string;
  photo: string;
  frame: AvatarFrameId;
  position: number;
  // Event specific
  score?: number;
  // Global/friends specific
  level?: number;
  leaguesAllTimeScore?: number;
};

/**
 * Get the weekly leaderboard ranking for the given event
 * @param eventId
 * @returns
 */
export function getWeeklyLeaderboardRanking(eventId?: string) {
  if (!eventId) {
    const event = getOngoingWeeklyLeaderboardEvent(user(), now());
    eventId = event?.id;
  }

  if (!eventId) {
    return undefined;
  }

  return useCache(
    async () => {
      const event = getOngoingWeeklyLeaderboardEvent(user(), now());
      const rankingArgs = getWeeklyLeaderboardRankingArgs(user(), eventId!);
      const result = rankingArgs
        ? await StateObserver.replicant.asyncGetters.getWeeklyLeaderboardRanking(
            rankingArgs,
          )
        : [];
      const allPlayers = result.map((x, i) => ({ ...x, position: i + 1 }));
      return {
        top3: allPlayers.slice(0, 3),
        rest: allPlayers.slice(3),
        player: allPlayers.find((x) => x.id === user().id),
        playerPosition: allPlayers.findIndex((x) => x.id === user().id) + 1,
        ended: eventId !== event?.id,
      };
    },
    `weeklyLeaderboardRanking-${eventId}`,
    duration({ seconds: 20 }),
  );
}

/**
 * Get the weekly leaderboard blobal players ranking
 * @returns
 */
export function getWeeklyLeaderboardGlobalRanking() {
  return useCache(
    async () => {
      const result =
        await StateObserver.replicant.asyncGetters.getWeeklyLeaderboardGlobalRanking();
      // Map to include position
      return result.map((x, i) => ({ ...x, position: i + 1 }));
    },
    `weeklyLeaderboardGlobalRanking`,
    duration({ minutes: 10 }),
  );
}

/**
 * Update cached global leaderboard with the user's current level
 * To prevent the user from being shown in the wrong position after leveling up
 */
export function updateUserInWeeklyLeaderboardGlobalCache() {
  const cached = getWeeklyLeaderboardGlobalRanking().getCached();
  if (!cached) return;
  const userId = user().id;
  const userIndex = cached.findIndex((x) => x.id === userId);
  if (userIndex === -1) return;
  const userEntry = cached[userIndex];
  if (!userEntry) return;
  userEntry.level = user().match2.level;
  userEntry.leaguesAllTimeScore = user().match2.leaguesAllTimeScore;
  cached.sort((a, b) => b.leaguesAllTimeScore - a.leaguesAllTimeScore);
  cached.sort((a, b) => b.level - a.level);
}

/**
 * Update cached friends leaderboard with the user's current level
 * To prevent the user from being shown in the wrong position after leveling up
 */
export function updateUserInWeeklyLeaderboardFriendsCache() {
  const cached = getWeeklyLeaderboardFriendsRanking().getCached();
  if (!cached) return;
  const userId = user().id;
  const userIndex = cached.findIndex((x) => x.id === userId);
  if (userIndex === -1) return;
  const userEntry = cached[userIndex];
  if (!userEntry) return;
  userEntry.level = user().match2.level;
  userEntry.leaguesAllTimeScore = user().match2.leaguesAllTimeScore;
  cached.sort((a, b) => b.leaguesAllTimeScore - a.leaguesAllTimeScore);
  cached.sort((a, b) => b.level - a.level);
}

/**
 * Get the weekly leaderboard friends ranking
 * @returns
 */
export function getWeeklyLeaderboardFriendsRanking() {
  return useCache(
    async () => {
      const result =
        await StateObserver.replicant.asyncGetters.getWeeklyLeaderboardFriendsRanking();
      // Map to include position
      return result.map((x, i) => ({ ...x, position: i + 1 }));
    },
    `weeklyLeaderboardfriendsRanking`,
    duration({ minutes: 10 }),
  );
}

/**
 * Get the event id of the weekly leaderboard event that the user can claim, if any
 * @returns The event id that can be claimed, or undefined if none
 */
export function getWeeklyLeaderboardEventToClaim() {
  const { weeklyLeaderboard } = user();

  if (!weeklyLeaderboard) {
    return undefined;
  }

  const event = getOngoingWeeklyLeaderboardEvent(user(), now());

  for (const [eventId, entry] of Object.entries(weeklyLeaderboard.entries)) {
    // Ignore ongoing event
    if (eventId === event?.id) {
      continue;
    }

    // If not claimed, return the event id
    if (!entry.claimedAt) {
      return eventId;
    }
  }

  return undefined;
}

/**
 * Run weekly leaderboard matchmaking with cache to prevent spamming
 */
export function runWeeklyleaderboardMatchMaking(eventId?: string) {
  const event = eventId
    ? weeklyLeaderboardSchedule.findEventByID(eventId)
    : getOngoingWeeklyLeaderboardEvent(user(), now());
  if (!event) return undefined;

  return useCache(
    async () => {
      // Must have a weekly leaderboard state
      const weeklyLeaderboard = user().weeklyLeaderboard;
      if (!weeklyLeaderboard) {
        return undefined;
      }

      // Must have joined the ongoing event
      if (!weeklyLeaderboard.entries[event.id]) {
        return undefined;
      }

      // Should have open spots for more opponents
      const opponents = weeklyLeaderboard.entries[event.id].opponents;
      if (opponents.length >= weeklyLeaderboardSize - 1) {
        return undefined;
      }

      // Run matchmaking
      await StateObserver.invoke.asyncWeeklyLeaderboardMatchmaking({ eventId });
      return event.id;
    },
    `weeklyLeaderboardMatchmaking-${event.id}`,
    duration({ minutes: 1 }),
  );
}
